import { Component, useEffect, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";
import { reportError } from "@/lib/monitoring";
import { TriangleAlert } from "lucide-react";
import { EmptyState, GlowButton } from "@/components/ui";

interface State {
  error: Error | null;
}

/** Catches render errors and shows a friendly glowing fallback instead of a blank screen. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("UI error", error, info.componentStack);
    reportError(error, { componentStack: info.componentStack });
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return <Fallback />;
  }
}

function Fallback() {
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <EmptyState
        icon={<TriangleAlert />}
        title="Something glitched"
        description="We hit an unexpected error. Reloading usually fixes it."
        action={<GlowButton onClick={() => window.location.reload()}>Reload page</GlowButton>}
      />
    </div>
  );
}

/** Router `errorElement`: the same friendly fallback for errors thrown while rendering a route. */
export function RouteError() {
  const error = useRouteError();
  useEffect(() => {
    console.error("Route error", error);
    reportError(error, { where: "route" });
  }, [error]);
  return <Fallback />;
}
